import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2, ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';
import {
  PLANNING_STATUSES,
  PLANNED_FORMATS,
  PRIORITIES,
  type PlanningItem,
} from '@/hooks/useContentPlanning';

interface Props {
  item: PlanningItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContentPlanningPreviewDialog({ item, open, onOpenChange }: Props) {
  const [fullscreen, setFullscreen] = useState(false);
  const [remoteHtml, setRemoteHtml] = useState<string | null>(null);
  const [loadingRemote, setLoadingRemote] = useState(false);

  const url = item?.url ?? null;
  const hasInlineHtml = !!item?.html_content;
  const isHtmlFile = !!url && /\.html?($|\?)/i.test(url);

  useEffect(() => {
    if (!open || hasInlineHtml || !url || !isHtmlFile) {
      setRemoteHtml(null);
      setLoadingRemote(false);
      return;
    }
    let cancelled = false;
    setRemoteHtml(null);
    setLoadingRemote(true);
    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        if (!cancelled) setRemoteHtml(text);
      })
      .catch(() => {
        if (!cancelled) setRemoteHtml(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingRemote(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, url, isHtmlFile, hasInlineHtml]);

  if (!item) return null;

  const status = PLANNING_STATUSES.find((s) => s.value === item.planning_status);
  const format = PLANNED_FORMATS.find((f) => f.value === item.planned_format);
  const prio = PRIORITIES.find((p) => p.value === item.priority);


  const renderContent = () => {
    if (item.vimeo_player_url) {
      return (
        <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
          <iframe
            src={item.vimeo_player_url}
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
    if (item.html_content) {
      const html = item.html_content;
      const interactiveFormats = ['recurso_interactivo_caso_uso', 'tutorial_paso_a_paso', 'simulador'];
      const isInteractiveFormat = item.planned_format
        ? interactiveFormats.includes(item.planned_format)
        : false;
      const looksLikeDocument = /<!doctype|<html|<script|<style/i.test(html);
      if (isInteractiveFormat || looksLikeDocument) {
        return (
          <iframe
            srcDoc={html}
            sandbox="allow-scripts allow-popups allow-forms"
            className="w-full rounded-lg border bg-white"
            style={{ height: fullscreen ? 'calc(100vh - 220px)' : '70vh' }}
            title={item.name}
          />
        );
      }
      return (
        <div
          className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-lg border bg-card"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
        />
      );
    }

    if (item.url) {
      const isImage = /\.(png|jpe?g|gif|webp|svg)$/i.test(item.url);
      const isPdf = /\.pdf($|\?)/i.test(item.url);
      if (isImage) {
        return <img src={item.url} alt={item.name} className="max-w-full rounded-lg" />;
      }
      if (isPdf) {
        return (
          <iframe src={item.url} className="w-full h-[70vh] rounded-lg border" title={item.name} />
        );
      }
      if (isHtmlFile && loadingRemote) {
        return (
          <div className="text-sm text-muted-foreground italic p-8 text-center border rounded-lg">
            Cargando contenido interactivo…
          </div>
        );
      }
      if (remoteHtml) {
        return (
          <iframe
            srcDoc={remoteHtml}
            sandbox="allow-scripts allow-popups allow-forms"
            className="w-full rounded-lg border bg-white"
            style={{ height: fullscreen ? 'calc(100vh - 220px)' : '70vh' }}
            title={item.name}
          />
        );
      }

      return (
        <div className="space-y-3">
          <iframe src={item.url} className="w-full h-[70vh] rounded-lg border" title={item.name} />
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Abrir en nueva pestaña
          </a>
        </div>
      );
    }
    if (item.thumbnail_url) {
      return <img src={item.thumbnail_url} alt={item.name} className="max-w-full rounded-lg" />;
    }
    return (
      <div className="text-sm text-muted-foreground italic p-8 text-center border rounded-lg">
        Este contenido aún no tiene URL, HTML embebido o video asociado para previsualizar.
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          fullscreen
            ? 'max-w-[100vw] w-screen h-screen sm:rounded-none p-6 overflow-y-auto'
            : 'max-w-4xl max-h-[90vh] overflow-y-auto'
        }
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="space-y-1 min-w-0">
              <DialogTitle className="truncate">{item.name}</DialogTitle>
              {item.description && (
                <DialogDescription className="line-clamp-2">{item.description}</DialogDescription>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={() => setFullscreen((f) => !f)}
              title={fullscreen ? 'Reducir' : 'Pantalla completa'}
            >
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 text-xs">
          {status && <Badge variant="outline" className={status.color}>{status.label}</Badge>}
          {format && <Badge variant="outline">{format.label}</Badge>}
          {prio && <Badge variant="outline" className={prio.color}>{prio.label}</Badge>}
          {item.category && <Badge variant="outline">{item.category}</Badge>}
          {item.responsible && <Badge variant="outline">👤 {item.responsible}</Badge>}
        </div>

        <div className="mt-2">{renderContent()}</div>

        {item.observations && (
          <div className="mt-4 text-sm">
            <div className="font-medium mb-1">Observaciones</div>
            <p className="text-muted-foreground whitespace-pre-wrap">{item.observations}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
